import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import { useQueryParamValue } from 'src/app/shared/hooks/location';
import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { PsfSharePageCombinedState, PsfSharePagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { PsfSharePageDataContextProvider } from './pageData';
import { PsfSharePageFcmContextProvider } from './pageFcm';
import { PsfSharePageFcmExperimentsContextProvider } from './pageFcmExperiments';
import { PsfSharePageStateContextProvider } from './pageState';

export type ErrorHandlerProps = { context: ErrorStateDetails<PsfSharePageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: PsfSharePagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function PsfSharePageContextProvider({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(slug, prefetchedData);
	const sourceLocation = useQueryParamValue('source_location');

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pageFcm, pageFcmExperiments } = pageContextState;

	return (
		<PsfSharePageDataContextProvider pageData={pageData}>
			<PsfSharePageFcmContextProvider pageFcm={pageFcm}>
				<PsfSharePageFcmExperimentsContextProvider pageFcmExperiments={pageFcmExperiments}>
					<PsfSharePageStateContextProvider>
						<TrackingContext
							properties={{
								petition_id: pageData.data.petition.id,
								signature_count: pageData.data.petition.signatureState.signatureCount.displayed,
								source_location: sourceLocation,
							}}
						>
							{children}
						</TrackingContext>
					</PsfSharePageStateContextProvider>
				</PsfSharePageFcmExperimentsContextProvider>
			</PsfSharePageFcmContextProvider>
		</PsfSharePageDataContextProvider>
	);
}
